import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { proposalsApi, eventProposalsApi, adminApi, syncApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import type { Proposal, EventProposal, SyncStatus, SyncLog, PotentialDuplicate } from "@/types";
import { Loader2, Clock, CheckCircle, XCircle, Users, CalendarDays, RefreshCw, Play, ArrowDown, ArrowUp, AlertCircle, FileText, Link2 } from "lucide-react";
import { format } from "date-fns";
import { formatTimeAgo } from "@/utils/timeAgo";
import { de } from "date-fns/locale";


interface CombinedProposal {
  id: number;
  type: 'contact' | 'event';
  proposed_by_user_id: number;
  proposed_by_name?: string;
  action: 'create' | 'update' | 'delete';
  status: 'pending' | 'approved' | 'rejected';
  title: string;
  subtitle?: string;
  created_at: string;
  updated_at: string;
  /** Only populated for `type === 'contact'` — surfaces the duplicate warning. */
  potential_duplicates?: PotentialDuplicate[];
  originalData: Proposal | EventProposal;
}

export function ProposalsPage() {
  const navigate = useNavigate();
  const { isAdmin, isApprover, user, hasModule } = useAuth();
  // Anträge gehören fachlich zu Kontakten/Kalender — deaktivierte Module
  // blenden ihre Anträge (und Filter) aus, statt 403s zu provozieren.
  const showContactProposals = hasModule('kontakte');
  const showEventProposals = hasModule('kalender');
  const [proposals, setProposals] = useState<CombinedProposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [proposalType, setProposalType] = useState<"all" | "contact" | "event">("all");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  
  // Sync status state
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isTriggering, setIsTriggering] = useState<string | null>(null);
  const [activeSyncLog, setActiveSyncLog] = useState<SyncLog | null>(null);
  const [syncElapsedSec, setSyncElapsedSec] = useState(0);

  // While a sync is running, tick the elapsed counter every second so the
  // UI can show "Running… 02:14" without polling the server.
  useEffect(() => {
    if (!activeSyncLog || activeSyncLog.completed_at) return;
    const startedAt = new Date(activeSyncLog.started_at).getTime();
    const tick = () => setSyncElapsedSec(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeSyncLog?.id, activeSyncLog?.completed_at]);

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!user) return; // wait for auth
    loadProposals();
    loadSyncStatus();
    const interval = setInterval(loadSyncStatus, 30000);
    return () => clearInterval(interval);
    // user (nicht nur user.id): nach /auth/me-Refresh können sich die
    // Modul-Zugriffe geändert haben und die Liste muss neu geladen werden.
  }, [user, isAdmin, isApprover]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [activeTab, proposalType]);

  const loadProposals = async () => {
    try {
      let combined: CombinedProposal[] = [];

      // Load contact proposals
      const contactResponse = showContactProposals
        ? (isAdmin
            ? await proposalsApi.getAll()
            : await proposalsApi.getMyProposals())
        : null;
      
      if (contactResponse?.data.data) {
        const contactProposals: CombinedProposal[] = contactResponse.data.data.map((p: Proposal) => ({
          id: p.id,
          type: 'contact' as const,
          proposed_by_user_id: p.proposed_by_user_id,
          proposed_by_name: p.proposed_by_name,
          action: p.action,
          status: p.status,
          title: (() => {
            const name = `${p.new_contact_data?.first_name || ''} ${p.new_contact_data?.last_name || ''}`.trim();
            if (name) return name;
            if (p.new_contact_data?.company_name) {
              return p.new_contact_data.department
                ? `${p.new_contact_data.company_name} — ${p.new_contact_data.department}`
                : p.new_contact_data.company_name;
            }
            if (p.new_contact_data?.email_address) return p.new_contact_data.email_address;
            return 'Unbenannter Kontakt';
          })(),
          subtitle: p.changes_summary && p.changes_summary.length > 0
            ? p.changes_summary.join(', ')
            : (p.new_contact_data?.email_address || p.new_contact_data?.company_name || ''),
          created_at: p.created_at,
          updated_at: p.updated_at,
          potential_duplicates: p.potential_duplicates ?? [],
          originalData: p
        }));
        combined = [...combined, ...contactProposals];
      }
      
      // Load event proposals
      const eventResponse = showEventProposals
        ? (isAdmin
            ? await eventProposalsApi.getAll()
            : await eventProposalsApi.getMyProposals())
        : null;
      
      if (eventResponse?.data.data) {
        const eventProposals: CombinedProposal[] = eventResponse.data.data.map((p: EventProposal) => ({
          id: p.id,
          type: 'event' as const,
          proposed_by_user_id: p.proposed_by_user_id,
          proposed_by_name: p.proposed_by_name,
          action: p.action,
          status: p.status,
          title: p.new_event_data.title || p.existing_title || 'Unbenannter Termin',
          subtitle: p.new_event_data.start_date 
            ? format(new Date(p.new_event_data.start_date), "dd.MM.yyyy", { locale: de })
            : undefined,
          created_at: p.created_at,
          updated_at: p.updated_at,
          originalData: p
        }));
        combined = [...combined, ...eventProposals];
      }
      
      // Sort by created_at desc
      combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      setProposals(combined);
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Failed to load proposals:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const response = await syncApi.getStatus();
      setSyncStatus(response.data.data);
    } catch (error) {
      console.error("Failed to load sync status:", error);
    }
  };

  const pollSyncLog = async (logId: number, label: string) => {
    // Poll /sync/logs every 2s up to 30 minutes. We track the most recent
    // row with this id and surface it in the UI. Bail early if the row
    // completes.
    const deadline = Date.now() + 30 * 60 * 1000;
    let lastCompleted: SyncLog | null = null;
    while (Date.now() < deadline) {
      try {
        const res = await syncApi.getLogs(1);
        const row = (res.data.data as SyncLog[]).find((r) => r.id === logId);
        if (row) {
          setActiveSyncLog(row);
          if (row.completed_at) {
            lastCompleted = row;
            break;
          }
        }
      } catch (err) {
        console.error('Failed to poll sync log:', err);
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    loadSyncStatus();
    loadProposals();
    if (lastCompleted) {
      if (lastCompleted.status === 'failed') {
        const details: any = lastCompleted.details || {};
        const stderr = details.spawn_stderr || details.spawn_stdout || '';
        const tail = stderr ? '\n\nServer-Output:\n' + String(stderr).slice(-400) : '';
        alert(`${label}-Sync fehlgeschlagen: ${lastCompleted.error_message || 'unbekannt'}${tail}`);
      } else if (lastCompleted.status !== 'success') {
        alert(`${label}-Sync beendet (Status: ${lastCompleted.status}).`);
      }
    } else {
      alert(`${label}-Sync läuft noch nach 30 Minuten. Bitte im Server-Log prüfen.`);
    }
  };

  const triggerSync = async (type: 'pull' | 'push' | 'full') => {
    if (!isAdmin) return;
    setIsTriggering(type);
    const label = type === 'full' ? 'Full' : type === 'pull' ? 'Pull' : 'Push';
    try {
      const res: any = await syncApi.trigger(type, '');
      const logId = res?.data?.data?.log_id;
      const childAlreadyExited = res?.data?.data?.child_already_exited;
      if (!logId) {
        // Fallback to the legacy hard-coded poll if the server didn't return
        // a log id (e.g. older deploy still in rotation).
        [2000, 5000, 10000, 15000].forEach((delay) => {
          setTimeout(() => {
            loadSyncStatus();
            loadProposals();
          }, delay);
        });
        return;
      }
      setActiveSyncLog({
        id: logId,
        operation_type: type,
        status: 'started',
        started_at: new Date().toISOString(),
        details: { execution_mode: 'cli_spawn' },
      });
      // Surface immediate spawn failures (e.g. FPM usage banner) without
      // waiting for the 30-minute poll.
      if (childAlreadyExited) {
        try {
          const r = await syncApi.getLogs(1);
          const row = (r.data.data as SyncLog[]).find((x) => x.id === logId);
          if (row) setActiveSyncLog(row);
        } catch (e) { /* ignore */ }
      }
      pollSyncLog(logId, label);
    } catch (error: any) {
      console.error('Failed to trigger sync:', error);
      const status = error.response?.status;
      const backend = error.response?.data;
      let msg = 'Sync-Fehler (Status: ' + (status || 'unknown') + ')';
      if (typeof backend === 'string' && backend) {
        msg = backend.substring(0, 200);
      } else if (backend && typeof backend === 'object') {
        msg = backend.message || backend.error || JSON.stringify(backend);
      } else if (error.message) {
        msg = error.message;
      }
      alert(msg || 'Unbekannter Fehler');
    } finally {
      setIsTriggering(null);
    }
  };

  const handleApprove = async (e: React.MouseEvent, proposal: CombinedProposal) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (proposal.type === 'contact') {
        await adminApi.approveProposal(proposal.id);
      } else {
        await eventProposalsApi.approve(proposal.id);
      }
      loadProposals();
    } catch (error) {
      console.error("Failed to approve:", error);
    }
  };

  const handleReject = async (e: React.MouseEvent, proposal: CombinedProposal) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (proposal.type === 'contact') {
        await adminApi.rejectProposal(proposal.id);
      } else {
        await eventProposalsApi.reject(proposal.id);
      }
      loadProposals();
    } catch (error) {
      console.error("Failed to reject:", error);
    }
  };

  // Bulk selection handlers
  const toggleSelection = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    e.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAllOnPage = () => {
    const newSelected = new Set(selectedIds);
    if (allCurrentPagePendingSelected) {
      currentPagePendingIds.forEach(id => newSelected.delete(id));
    } else {
      currentPagePendingIds.forEach(id => newSelected.add(id));
    }
    setSelectedIds(newSelected);
  };


  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setIsProcessingBulk(true);
    try {
      // Separate contact and event proposals
      const selectedProposals = proposals.filter(p => selectedIds.has(p.id));
      const contactIds = selectedProposals.filter(p => p.type === 'contact').map(p => p.id);
      
      if (contactIds.length > 0) {
        await adminApi.bulkApprove(contactIds);
      }
      
      // Event proposals need individual approval
      const eventProposals = selectedProposals.filter(p => p.type === 'event');
      for (const ep of eventProposals) {
        await eventProposalsApi.approve(ep.id);
      }
      
      loadProposals();
    } catch (error) {
      console.error("Failed to bulk approve:", error);
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.size === 0) return;
    setIsProcessingBulk(true);
    try {
      const selectedProposals = proposals.filter(p => selectedIds.has(p.id));
      const contactIds = selectedProposals.filter(p => p.type === 'contact').map(p => p.id);
      
      if (contactIds.length > 0) {
        await adminApi.bulkReject(contactIds);
      }
      
      const eventProposals = selectedProposals.filter(p => p.type === 'event');
      for (const ep of eventProposals) {
        await eventProposalsApi.reject(ep.id);
      }
      
      loadProposals();
    } catch (error) {
      console.error("Failed to bulk reject:", error);
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case "approved":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "Ausstehend";
      case "approved":
        return "Genehmigt";
      case "rejected":
        return "Abgelehnt";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getActionText = (action: string) => {
    switch (action) {
      case "create":
        return "Neu";
      case "update":
        return "Aktualisierung";
      case "delete":
        return "Löschung";
      default:
        return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "create":
        return "bg-green-100 text-green-800";
      case "update":
        return "bg-yellow-100 text-yellow-800";
      case "delete":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };



  const filteredProposals = proposals.filter((p) => {
    const statusMatch = activeTab === "all" || p.status === activeTab;
    const typeMatch = proposalType === "all" || p.type === proposalType;
    return statusMatch && typeMatch;
  });

  const totalPages = Math.ceil(filteredProposals.length / ITEMS_PER_PAGE) || 1;
  const paginatedProposals = filteredProposals.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const pendingCount = proposals.filter((p) => p.status === "pending").length;
  const currentPagePendingIds = paginatedProposals
    .filter(p => p.status === "pending")
    .map(p => p.id);
  const allCurrentPagePendingSelected = currentPagePendingIds.length > 0 &&
    currentPagePendingIds.every(id => selectedIds.has(id));
  const mySyncState = syncStatus?.user_states.find((u) => u.email === user?.email);

  return (
    <div className="space-y-6">
      {/* Header with Sync Status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Anträge</h1>
          <p className="text-sm text-gray-500 mt-1">
            {pendingCount > 0 ? `${pendingCount} ausstehende Anträge` : "Keine ausstehenden Anträge"}
          </p>
        </div>
        
        {/* Sync Status Card */}
        <div className="flex flex-col gap-2 bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm min-w-[280px]">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {activeSyncLog && !activeSyncLog.completed_at ? (
                <RefreshCw className="h-4 w-4 text-accent-600 animate-spin" />
              ) : activeSyncLog?.status === 'failed' ? (
                <AlertCircle className="h-4 w-4 text-red-500" />
              ) : activeSyncLog?.status === 'success' ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : mySyncState?.is_syncing ? (
                <RefreshCw className="h-4 w-4 text-accent-600 animate-spin" />
              ) : mySyncState?.last_error ? (
                <AlertCircle className="h-4 w-4 text-red-500" />
              ) : (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
              <span className="text-sm font-medium text-gray-700">
                {activeSyncLog && !activeSyncLog.completed_at
                  ? `Sync läuft… (${activeSyncLog.operation_type.toUpperCase()})`
                  : activeSyncLog?.status === 'failed'
                    ? `Sync fehlgeschlagen (${activeSyncLog.operation_type.toUpperCase()})`
                    : activeSyncLog?.status === 'success'
                      ? `Sync OK (${activeSyncLog.operation_type.toUpperCase()})`
                      : mySyncState?.is_syncing
                        ? 'Synchronisiert…'
                        : mySyncState?.last_error
                          ? 'Sync-Fehler'
                          : 'Sync aktuell'}
              </span>
            </div>

            {isAdmin && (
              <div className="flex items-center gap-1 border-l pl-3 ml-1 flex-wrap">
                <button
                  onClick={() => triggerSync('pull')}
                  disabled={!!isTriggering}
                  className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded disabled:opacity-50"
                  title="Pull (Outlook → DB)"
                >
                  {isTriggering === 'pull' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDown className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => triggerSync('push')}
                  disabled={!!isTriggering}
                  className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded disabled:opacity-50"
                  title="Push (DB → Outlook)"
                >
                  {isTriggering === 'push' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => triggerSync('full')}
                  disabled={!!isTriggering}
                  className="p-1.5 text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50"
                  title="Full Sync"
                >
                  {isTriggering === 'full' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                </button>
              </div>
            )}
          </div>

          {/* Progress line: shows elapsed time, type, and the row's
              current status while a sync is in flight or just finished. */}
          {activeSyncLog && (
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <Clock className="h-3.5 w-3.5" />
              <span>Log #{activeSyncLog.id}</span>
              {!activeSyncLog.completed_at ? (
                <>
                  <span aria-hidden>•</span>
                  <span>Läuft seit {formatDuration(syncElapsedSec)}</span>
                  <span aria-hidden>•</span>
                  <span>gestartet {format(new Date(activeSyncLog.started_at), 'HH:mm:ss')}</span>
                </>
              ) : (
                <>
                  <span aria-hidden>•</span>
                  <span>{formatDuration(syncElapsedSec)}</span>
                  <span aria-hidden>•</span>
                  <span>beendet {format(new Date(activeSyncLog.completed_at), 'HH:mm:ss')}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Spawn / Sync Error Message — shown when the just-triggered row
          captured a non-empty error or stderr. We surface the captured
          spawn_stderr (last 400 chars) so the admin can see *why* a
          click produced a row stuck on 'started' without SSH access. */}
      {activeSyncLog && (activeSyncLog.error_message || (activeSyncLog.details as any)?.spawn_stderr) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-800">
                Synchronisierungsfehler (Log #{activeSyncLog.id})
              </p>
              {activeSyncLog.error_message && (
                <p className="text-sm text-red-600 mt-1">{activeSyncLog.error_message}</p>
              )}
              {(activeSyncLog.details as any)?.spawn_stderr && (
                <details className="mt-2">
                  <summary className="text-xs text-red-700 cursor-pointer hover:underline">
                    Server-Output anzeigen
                  </summary>
                  <pre className="mt-2 text-xs text-red-700 bg-red-100/60 p-2 rounded overflow-x-auto whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
{String((activeSyncLog.details as any).spawn_stderr).slice(-1500)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legacy error path — kept so non-triggered failures still show. */}
      {mySyncState?.last_error && !activeSyncLog && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Synchronisierungsfehler</p>
              <p className="text-sm text-red-600 mt-1">{mySyncState.last_error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-x-8">
          {[
            { key: "all", label: "Alle" },
            { key: "pending", label: "Ausstehend" },
            { key: "approved", label: "Genehmigt" },
            { key: "rejected", label: "Abgelehnt" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Proposal Type Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Filter:</span>
        <div className="flex bg-gray-100 rounded-lg p-1">
          {[
            { key: "all", label: "Alle", icon: FileText, show: true },
            { key: "contact", label: "Kontakte", icon: Users, show: showContactProposals },
            { key: "event", label: "Termine", icon: CalendarDays, show: showEventProposals },
          ].filter((type) => type.show).map((type) => (
            <button
              key={type.key}
              onClick={() => setProposalType(type.key as typeof proposalType)}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                proposalType === type.key 
                  ? "bg-white text-gray-900 shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <type.icon className="h-4 w-4 mr-1.5" />
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions for Admin */}
      {isApprover && selectedIds.size > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm text-gray-700">
            {selectedIds.size} Anträge ausgewählt
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleBulkApprove}
              disabled={isProcessingBulk}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {isProcessingBulk ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-1" />
              )}
              Genehmigen
            </button>
            <button
              onClick={handleBulkReject}
              disabled={isProcessingBulk}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Ablehnen
            </button>
          </div>
        </div>
      )}

      {/* Proposals List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : filteredProposals.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">
            {!showContactProposals && !showEventProposals
              ? "Keine Anträge verfügbar."
              : activeTab === "all"
              ? "Keine Anträge vorhanden"
              : `Keine ${getStatusText(activeTab).toLowerCase()}en Anträge`}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isApprover && currentPagePendingIds.length > 0 && (
            <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
              <input
                type="checkbox"
                checked={allCurrentPagePendingSelected}
                onChange={toggleSelectAllOnPage}
                className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-600"
              />
              <span className="text-sm text-gray-700">
                {allCurrentPagePendingSelected ? "Alle abwählen" : "Alle auswählen"} ({currentPagePendingIds.length} ausstehend auf dieser Seite)
              </span>
            </div>
          )}
          <ul className="divide-y divide-gray-200">
            {paginatedProposals.map((proposal) => (
              <li key={`${proposal.type}-${proposal.id}`} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => proposal.type === 'contact' ? navigate(`/proposals/${proposal.id}`) : undefined}>
                <div className="block p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {/* Checkbox for admin on pending proposals */}
                        {isApprover && proposal.status === "pending" && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(proposal.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => toggleSelection(e, proposal.id)}
                            className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-600"
                          />
                        )}

                        {/* Type Badge */}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          proposal.type === 'contact' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {proposal.type === 'contact' ? 'Kontakt' : 'Termin'}
                        </span>

                        {/* Action Badge */}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(proposal.action)}`}>
                          {getActionText(proposal.action)}
                        </span>

                        {/* Status Badge */}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                          {getStatusText(proposal.status)}
                        </span>

                        {/* Submitter info (for admins) */}
                        {isAdmin && proposal.proposed_by_name && proposal.proposed_by_name !== user?.name && (
                          <span className="text-xs text-gray-500">
                            von {proposal.proposed_by_name}
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-medium text-gray-900">{proposal.title}</h3>

                      {/* Duplicate-warning pill for contact proposals that
                          have at least one potential existing match. Clicking
                          jumps straight to the detail page where the
                          comparison panel is. */}
                      {proposal.type === 'contact' && proposal.potential_duplicates && proposal.potential_duplicates.length > 0 && (() => {
                        const top = proposal.potential_duplicates.slice(0, 3);
                        const strong = top.some(c => c.match_score >= 60);
                        return (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                strong
                                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                  : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                              }`}
                              title={top.map(c => `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() + (c.company_name ? ` (${c.company_name})` : '')).join('\n')}
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              {top.length === 1
                                ? <>Ähnlich: <span className="ml-1 font-semibold">{top[0].first_name} {top[0].last_name}</span>{top[0].company_name ? ` (${top[0].company_name})` : ''}</>
                                : <>Ähnlich: {top.length} Kontakte</>}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/proposals/${proposal.id}`); }}
                              className="text-xs text-amber-700 hover:text-amber-900 underline"
                            >
                              vergleichen
                            </button>
                          </div>
                        );
                      })()}

                      {proposal.subtitle && (
                        <p className="text-sm text-gray-600">{proposal.subtitle}</p>
                      )}

                      <p className="mt-2 text-xs text-gray-400">
                        Eingereicht {formatTimeAgo(proposal.created_at)}
                      </p>
                    </div>

                    {/* Admin Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isAdmin && proposal.status === "pending" ? (
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => handleApprove(e, proposal)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Genehmigen
                          </button>
                          <button
                            onClick={(e) => handleReject(e, proposal)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Ablehnen
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          {getStatusIcon(proposal.status)}
                          <span className="text-sm font-medium hidden sm:inline">{getStatusText(proposal.status)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Seite {currentPage} von {totalPages} ({filteredProposals.length} gesamt)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Zurück
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
